import { useEffect, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api, ApiError, hasRememberedSession } from '@/lib/api'
import { MIN_SERVER_QUERY, parseRecent, type PaletteItem, type SearchResponse } from './searchModel'

/**
 * Whether this server has no `/api/search` at all.
 *
 * The web and the API deploy apart, so a new palette can meet an old API. A
 * 404 on the endpoint itself says so, and once it has been said there is no
 * point asking again on every keystroke for the rest of the session — the
 * palette carries on with pages and actions, which is still most of its job.
 * Module-level so a palette reopened later remembers.
 */
let endpointMissing = false

export function useServerSearch(query: string) {
  const q = query.trim()
  const [missing, setMissing] = useState(endpointMissing)
  const wanted = q.length >= MIN_SERVER_QUERY && !missing

  const result = useQuery({
    queryKey: ['search', q],
    queryFn: async ({ signal }) =>
      (await api.get<SearchResponse>('/search', { params: { q, limit: 5 }, signal })).data,
    enabled: wanted,
    // A query is retyped often — backspace, then the same letter again — and
    // the answer a few seconds ago is still the answer.
    staleTime: 30_000,
    // A refused search is not worth three more tries while somebody types; the
    // next keystroke is the retry.
    retry: false,
    // The previous rows stay while the next ones load, so the list does not
    // collapse and re-grow under the cursor on every letter.
    placeholderData: keepPreviousData,
  })

  const notFound = result.error instanceof ApiError && result.error.status === 404

  useEffect(() => {
    if (notFound) {
      endpointMissing = true
      setMissing(true)
    }
  }, [notFound])

  return {
    data: wanted ? result.data : undefined,
    loading: wanted && result.isFetching,
    // A real failure, said once under the box. A missing endpoint is not
    // one — it is an older server, and the palette simply has less to say.
    unavailable: wanted && result.isError && !notFound,
  }
}

// ---------------------------------------------------------------------------
// Recently opened, per person, and only where the session is remembered
// ---------------------------------------------------------------------------

/*
 * The same rule as the remembered route in lib/lastRoute.ts, for the same
 * reason. Shop-floor terminals are shared, and "do not remember me" is how a
 * person says so; a list of the reports and people they just looked at is
 * exactly the trail that choice is meant to avoid leaving. Keyed by user as
 * well, so the next person to sign in on a remembered device does not see it.
 */
const recentKey = (userId: string) => `kapro.search.recent.${userId}`

export function loadRecent(userId: string | undefined): PaletteItem[] {
  if (!userId) return []

  try {
    if (!hasRememberedSession()) return []

    return parseRecent(localStorage.getItem(recentKey(userId)))
  } catch {
    // Private mode, blocked storage: there is simply no history.
    return []
  }
}

export function saveRecent(userId: string | undefined, items: PaletteItem[]): void {
  if (!userId) return

  try {
    if (!hasRememberedSession()) return

    localStorage.setItem(recentKey(userId), JSON.stringify(items))
  } catch {
    // Full or blocked storage costs the history, never the navigation.
  }
}
